import { createContext, PropsWithChildren, useCallback, useContext, useState } from 'react';

import {
  ScenarioFormData,
  Step1DataForm,
  Step2DataForm,
  Step3DataForm,
  Step4DataForm,
} from './types/form';

type EditScenarioProvider = {
  initValues?: { formData: ScenarioFormData; scenarioDbId: string; scenarioDtoId: string };
} & PropsWithChildren;

type EditScenarioContext = {
  step: number;
  scenario: ScenarioFormData;
  setStep: (step: number) => void;
  setStepData: (props: SetStepDataProps) => void;
  isEdit: boolean;
  scenarioDbId?: string;
  scenarioDtoId?: string;
};

const EditScenarioContextInitialValues: EditScenarioContext = {
  scenario: {
    step1: undefined,
    step2: undefined,
    step3: undefined,
    step4: undefined,
  },
  setStepData: () => {},
  setStep: () => {},
  step: 1,
  isEdit: false,
  scenarioDbId: undefined,
  scenarioDtoId: undefined,
};

const EditScenarioContext = createContext<EditScenarioContext>(EditScenarioContextInitialValues);

type SetStepDataProps =
  | { step: 1; data: Step1DataForm }
  | { step: 2; data: Step2DataForm }
  | { step: 3; data: Step3DataForm }
  | { step: 4; data: Step4DataForm };

export const EditScenarioProvider = ({ children, initValues }: EditScenarioProvider) => {
  const [scenario, setScenario] = useState<ScenarioFormData>(
    initValues?.formData ?? EditScenarioContextInitialValues.scenario,
  );

  const [step, setStep] = useState(1);

  const setStepData = useCallback((props: SetStepDataProps) => {
    const stepName = `step${props.step}`;

    setScenario((prev) => ({ ...prev, [stepName]: props.data }));
  }, []);

  return (
    <EditScenarioContext.Provider
      value={{
        step,
        setStep,
        scenario,
        setStepData,
        isEdit: Boolean(initValues),
        scenarioDbId: initValues?.scenarioDbId,
        scenarioDtoId: initValues?.scenarioDtoId,
      }}
    >
      {children}
    </EditScenarioContext.Provider>
  );
};

export const useEditScenarioContext = () => useContext(EditScenarioContext);
